import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, Copy, Check } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useTranslation } from '../../i18n/LocaleContext';

const AdminWithdrawals = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });

  useEffect(() => {
    fetchWithdrawals();
  }, [filter, page]);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const data = await adminService.getWithdraws(filter, 50, page);
      setWithdrawals(data.withdraws || []);
      setPagination(data.pagination || { total: 0, total_pages: 1 });
    } catch (error) {
      console.error('Fetch withdrawals error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleApprove = async (withdrawId) => {
    if (!window.confirm(t('aWdApproveConfirm'))) return;

    try {
      setProcessing(withdrawId);
      await adminService.approveWithdraw(withdrawId);
      alert(t('aWdApproved'));
      fetchWithdrawals();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (withdrawId) => {
    if (!window.confirm(t('aWdRejectConfirm'))) return;

    try {
      setProcessing(withdrawId);
      await adminService.rejectWithdraw(withdrawId, '');
      alert(t('aWdRejectedRefunded'));
      fetchWithdrawals();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-900 bg-opacity-30 border border-yellow-700', text: 'text-yellow-400', label: t('aWdPending'), icon: Clock },
      approved: { bg: 'bg-green-900 bg-opacity-30 border border-green-700', text: 'text-green-400', label: t('aWdCompleted'), icon: CheckCircle },
      rejected: { bg: 'bg-red-900 bg-opacity-30 border border-red-700', text: 'text-red-400', label: t('aWdRejected'), icon: XCircle }
    };
    const badge = badges[status];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aWdTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aWdSubtitle')}</p>
      </div>

      {/* Alert */}
      {pendingCount > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-gray-400" />
            <p className="text-sm text-gray-400">
              <strong>{t('aWdPendingCount', { n: pendingCount })}</strong> {t('aWdPendingSuffix')}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-6 border border-gray-700">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => { setFilter('all'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'all' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aWdAll')}
          </button>
          <button
            onClick={() => { setFilter('pending'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'pending' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aWdPending')}
          </button>
          <button
            onClick={() => { setFilter('approved'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'approved' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aWdCompleted')}
          </button>
          <button
            onClick={() => { setFilter('rejected'); setPage(1); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === 'rejected' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {t('aWdRejected')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aWdUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aWdAmount')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aWdReceiveMethod')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aWdStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aWdDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aWdActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                    {t('aWdLoading')}
                  </td>
                </tr>
              ) : withdrawals.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                    {t('aWdEmpty')}
                  </td>
                </tr>
              ) : withdrawals.map((withdrawal) => (
                <tr key={withdrawal.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-300">#{withdrawal.id}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-white">{withdrawal.username}</p>
                      <p className="text-xs text-gray-400">{withdrawal.phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-white">{formatCurrency(withdrawal.amount)}</td>
                  <td className="px-6 py-4">
                    {withdrawal.withdraw_method === 'usdt' ? <div className="space-y-1"><p className="text-sm font-medium text-yellow-400">USDT · {withdrawal.network}</p><div className="flex items-center gap-2"><p className="max-w-48 truncate text-xs font-mono text-gray-300">{withdrawal.wallet_address}</p><button onClick={() => handleCopy(withdrawal.wallet_address, `wallet-${withdrawal.id}`)} className="p-1 hover:bg-gray-600 rounded" title={t('aWdCopyWallet')}>{copiedId === `wallet-${withdrawal.id}` ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-gray-400" />}</button></div></div> : <div className="space-y-1"><p className="text-sm text-white font-medium">{withdrawal.bank_name}</p><div className="flex items-center gap-2"><p className="text-xs text-gray-400 font-mono">{withdrawal.account_number}</p><button onClick={() => handleCopy(withdrawal.account_number, `acc-${withdrawal.id}`)} className="p-1 hover:bg-gray-600 rounded" title={t('aWdCopyStk')}>{copiedId === `acc-${withdrawal.id}` ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3 text-gray-400" />}</button></div><p className="text-xs text-gray-500">{withdrawal.account_name}</p></div>}
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(withdrawal.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{new Date(withdrawal.created_at).toLocaleString('vi-VN')}</td>
                  <td className="px-6 py-4">
                    {withdrawal.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(withdrawal.id)}
                          disabled={processing === withdrawal.id}
                          className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600 disabled:opacity-50"
                        >
                          {processing === withdrawal.id ? t('aWdProcessing') : t('aWdApprove')}
                        </button>
                        <button
                          onClick={() => handleReject(withdrawal.id)}
                          disabled={processing === withdrawal.id}
                          className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 disabled:opacity-50"
                        >
                          {t('aWdRejected')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {pagination.total > 0 && <div className="mt-4 flex items-center justify-between text-sm text-gray-400"><span>{t('aWdPaginationTotal', { total: pagination.total, page, pages: pagination.total_pages })}</span><div className="flex gap-2"><button onClick={() => setPage(page - 1)} disabled={page === 1} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aWdPrev')}</button><button onClick={() => setPage(page + 1)} disabled={page >= pagination.total_pages} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aWdNext')}</button></div></div>}
    </div>
  );
};

export default AdminWithdrawals;
